/**
 * @fileoverview GlobalModalContext component that provides modal functionality with transitions
 * Wraps child components with a modal context provider and handles modal state management
 * @example
 * // Usage in a component:
 * import { GlobalModalContext } from './GlobalModalContext'
 *
 * function App() {
 *   return (
 *     <GlobalModalContext>
 *       <YourComponent />
 *     </GlobalModalContext>
 *   )
 * }
 *
 * // Opening a modal from a child component:
 * import { useContext } from 'react'
 * import { ModalContext } from '../ModalContext'
 *
 * function YourComponent() {
 *   const modal = useContext(ModalContext)
 *
 *   return (
 *     <button onClick={() => modal.open({
 *       render: () => <div>Modal Content</div>
 *     })}>
 *       Open Modal
 *     </button>
 *   )
 * }
 */


import { PropsWithChildren, useState } from 'react'
import { CSSTransition } from 'react-transition-group'
import { useActivityPing } from '../hooks/useActivityPing'
import { ModalAPI, ModalConfig, ModalContext } from '../ModalContext'
import css from './GlobalModalContext.module.css'
import { Modal } from './questions/Modal'

/**
 * @function GlobalModalContext
 * @description Provides a modal context with transition animations
 * @param {PropsWithChildren} children - The child components to wrap with the modal context
 * @returns {JSX.Element} The GlobalModalContext component
 */
export function GlobalModalContext({ children }: PropsWithChildren) {
    const ping = useActivityPing()
    const [modalConfig, setModalConfig] = useState<ModalConfig>({
        render: () => <></>,
    })

    const [isOpen, setIsOpen] = useState<boolean>(false)

    const API: ModalAPI = {
        async open(modalConfig: ModalConfig) {
            setModalConfig(modalConfig)
            setIsOpen(true)
        },
        async close() {
            setIsOpen(false)
            await ping()
        },
    }

    function onConfirm() {
        setIsOpen(false)
        return Promise.resolve()
    }

    function onCancel() {
        setIsOpen(false)
        return Promise.resolve()
    }

    function onClose() {
        modalConfig.onClose?.()
        setIsOpen(false)
        return Promise.resolve()
    }

    return (
        <ModalContext.Provider value={API}>
            {children}
            <CSSTransition
                in={isOpen}
                timeout={300}
                mountOnEnter
                unmountOnExit
                classNames={{
                    enter: css.transition_enter,
                    enterActive: css.transition_enter_active,
                    exit: css.transition_exit,
                    exitActive: css.transition_exit_active,
                }}
            >
                <Modal
                    render={modalConfig.render}
                    canClose={modalConfig.canClose}
                    onConfirm={onConfirm}
                    onCancel={onCancel}
                    onClose={onClose}
                />
            </CSSTransition>
        </ModalContext.Provider>
    )
}
